import React, {useState} from 'react';
import Lottie from 'lottie-react';
import {CardColor, CARD_COLORS} from '../config';
import WebAudioPlayer from '../../WebStoryScreen/Components/WebAudioPlayer';
import WebShadowButton from '../../WebStoryScreen/Components/WebShadowButton';

import yellowUnselected from '../../../appAssets/jsons/yellow-unselected.json';
import yellowSelected from '../../../appAssets/jsons/yellow-selected.json';
import blueUnselected from '../../../appAssets/jsons/blue-unselected.json';
import blueSelected from '../../../appAssets/jsons/blue-selected.json';
import orangeUnselected from '../../../appAssets/jsons/orange-unselected.json';
import orangeSelected from '../../../appAssets/jsons/orange-selected.json';
import redUnselected from '../../../appAssets/jsons/red-unselected.json';
import redSelected from '../../../appAssets/jsons/red-selected.json';

interface CardGameProps {
  onCardPicked: (card: CardColor) => void;
  initialSelectedCard?: CardColor;
  hidePickButton?: boolean;
  tableHeight?: number;
}

const CARDS: CardColor[] = [...CARD_COLORS];

const CARD_ANIMS: Record<CardColor, {normal: object; selected: object}> = {
  yellow: {normal: yellowUnselected, selected: yellowSelected},
  blue:   {normal: blueUnselected,   selected: blueSelected},
  orange: {normal: orangeUnselected, selected: orangeSelected},
  red:    {normal: redUnselected,    selected: redSelected},
};

const TAP_SOUND_URI = 'https://assets.prod.yetimyfriend.com/select_object.mp3';
const SELECTED_SCALE = 1.15;

const CardGame: React.FC<CardGameProps> = ({
  onCardPicked,
  initialSelectedCard,
  hidePickButton,
  tableHeight,
}) => {
  const isLarge = window.innerWidth >= 768;
  const tH = tableHeight ?? Math.round(window.innerHeight * 0.38);

  const [selectedCard, setSelectedCard] = useState<CardColor | null>(
    initialSelectedCard ?? null,
  );
  const [tapSound, setTapSound] = useState<string | null>(null);

  const handleCardPress = (card: CardColor) => {
    if (card === selectedCard) return;
    setTapSound(`${TAP_SOUND_URI}?t=${Date.now()}`);
    setSelectedCard(card);
  };

  const cardSize = isLarge ? {width: 140, height: 165} : {width: 100, height: 118};

  const cardsRowStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: Math.round(tH * 0.35),
    left: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    alignItems: 'flex-end',
    paddingLeft: isLarge ? 32 : 16,
    paddingRight: isLarge ? 32 : 16,
  };

  const buttonContainerStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: 16,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'center',
    pointerEvents: 'auto',
  };

  return (
    <div style={{position: 'absolute', inset: 0, pointerEvents: 'none'}}>
      <div style={cardsRowStyle}>
        {CARDS.map(card => {
          const isSelected = card === selectedCard;
          const anim = isSelected
            ? CARD_ANIMS[card].selected
            : CARD_ANIMS[card].normal;
          return (
            <div
              key={card}
              style={{
                ...cardSize,
                cursor: hidePickButton ? 'default' : 'pointer',
                transform: isSelected ? `scale(${SELECTED_SCALE})` : 'scale(1)',
                transition: 'transform 0.25s cubic-bezier(0.34,1.56,0.64,1)',
                pointerEvents: 'auto',
              }}
              onClick={() => !hidePickButton && handleCardPress(card)}>
              <Lottie
                animationData={anim}
                style={{width: '100%', height: '100%'}}
                loop
              />
            </div>
          );
        })}
      </div>

      {selectedCard && !hidePickButton && (
        <div style={buttonContainerStyle}>
          <WebShadowButton
            title="Yes, I pick this card!"
            variant="text"
            labelStyle={{ fontFamily: 'Mochiy Pop One', fontSize: isLarge ? 17 : 14 }}
            onPress={() => onCardPicked(selectedCard)}
          />
        </div>
      )}

      {tapSound ? <WebAudioPlayer key={tapSound} uri={tapSound} /> : null}
    </div>
  );
};

export default CardGame;
